import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DISPLAY_MODE_KEY,
  DisplayMode,
  PrefsContext,
  UI_THEME_KEY,
  UiTheme,
  applyDisplayMode,
  applyUiTheme,
} from '../hooks/preferences';

const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [theme, setTheme] = useState<UiTheme>('default');
  const [mode, setMode] = useState<DisplayMode>('light');

  // On mount: sync React state with whatever was already applied from localStorage
  // (the no-flash inline script in gatsby-ssr.tsx handles the classes before hydration).
  useEffect(() => {
    const savedTheme = (localStorage.getItem(UI_THEME_KEY) as UiTheme | null) ?? 'default';
    const savedMode = (localStorage.getItem(DISPLAY_MODE_KEY) as DisplayMode | null) ?? 'light';
    setTheme(savedTheme);
    setMode(savedMode);
    applyUiTheme(savedTheme);
    applyDisplayMode(savedMode);
  }, []);

  const handleSetTheme = useCallback((t: UiTheme) => {
    setTheme(t);
    localStorage.setItem(UI_THEME_KEY, t);
    applyUiTheme(t);
  }, []);

  const handleSetMode = useCallback((m: DisplayMode) => {
    setMode(m);
    localStorage.setItem(DISPLAY_MODE_KEY, m);
    applyDisplayMode(m);
  }, []);

  const value = useMemo(
    () => ({ theme, mode, setTheme: handleSetTheme, setMode: handleSetMode }),
    [theme, mode, handleSetTheme, handleSetMode],
  );

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
};

export { ThemeProvider };
export default ThemeProvider;
